import {
  ButtonVariant,
  Device,
  FloatingMessageVariant,
  Layout,
  LineItem,
  LineItemSizeVariant,
  ResponsiveTypography,
  ResponsiveTypographyVariant,
  TEventFunction,
  useDeviceViewport,
} from '@se-admin/falcon';
import { i18n } from '@secret-escapes/i18n';
import * as React from 'react';
import { withTheme } from 'styled-components';
import { TranslationsContext } from '../../common/contexts/TranslationsContext';
import { CancellationConfirmation } from '../CancellationPage/CancellationConfirmation/CancellationConfirmation';
import {
  DisclaimerText,
  StyledFloatingMessage,
  WrapperDiv,
} from '../CancellationPage/CancellationPage.styles';
import { ICancellationSummaryProps } from './CancellationSummary.interfaces';
import {
  BackButton,
  BreakdownSection,
  ExplanationTypography,
  HorizontalSeparator,
  InlineLineItemContainer,
  LineItemContainer,
  OptionsButton,
  PriceBreakdown,
  StyledButton,
  StyledTypography,
} from './CancellationSummary.styles';

const CancellationSummary: React.FunctionComponent<ICancellationSummaryProps> = ({
  cancellationSummary,
  theme,
  handleCancel,
  handleToggleCreditRefund,
  handleConfirm,
  creditRefund,
  cancellationRequested,
}) => {
  const { translations } = React.useContext(TranslationsContext);
  const deviceViewport: Device = useDeviceViewport();
  const isMobile: boolean = deviceViewport === Device.mobile;
  const popupTipClose: React.MutableRefObject<TEventFunction> = React.createRef();
  const handleClose: () => void = () => {
    popupTipClose.current({ type: 'click' });
  };
  window.scrollTo(0, 0);

  const titleVariant: ResponsiveTypographyVariant = isMobile
    ? ResponsiveTypographyVariant.t3Serif
    : ResponsiveTypographyVariant.t4Serif;

  return (
    <React.Fragment>
      {!isMobile && (
        <BackButton variant={ButtonVariant.text} onClick={handleCancel}>
          {`< ${i18n('cancellation.back.to.bookings', [], translations)}`}
        </BackButton>
      )}
      <BreakdownSection>
        <StyledTypography color={theme.color.grey60} variant={titleVariant}>
          {cancellationSummary.editorial.header}
        </StyledTypography>
        <ExplanationTypography
          color={theme.color.grey60}
          variant={ResponsiveTypographyVariant.body}
          allowDangerousRender={true}
        >
          {cancellationSummary.editorial.summary}
        </ExplanationTypography>
        <ResponsiveTypography
          color={theme.color.grey60}
          variant={ResponsiveTypographyVariant.bodyBold}
        >
          {cancellationSummary.editorial.refundSummary}
        </ResponsiveTypography>
        <PriceBreakdown>
          <LineItem
            title={i18n('cancellation.paid', [], translations)}
            value={cancellationSummary.paidBreakdown?.total?.forDisplay}
            bold={true}
            sizeVariant={LineItemSizeVariant.medium}
          />
          {cancellationSummary.paidBreakdown?.items?.map((props, index) => {
            return (
              <LineItemContainer key={index}>
                <LineItem
                  title={props.concept}
                  value={props.price.forDisplay}
                  bold={false}
                  sizeVariant={LineItemSizeVariant.small}
                />
              </LineItemContainer>
            );
          })}
          <HorizontalSeparator />
          <LineItemContainer>
            <LineItem
              title={i18n('cancellation.total.refund', [], translations)}
              subcontent={cancellationSummary.editorial.refundTotalDescription}
              value={cancellationSummary.toBeRefundedBreakdown?.total?.forDisplay}
              valueColor={theme.color.primary}
              bold={true}
              sizeVariant={LineItemSizeVariant.medium}
            />
          </LineItemContainer>
          {cancellationSummary.toBeRefundedBreakdown?.items?.map((props, index) => {
            return (
              <InlineLineItemContainer key={index}>
                <LineItem
                  title={props.concept}
                  value={props.price.forDisplay}
                  bold={false}
                  sizeVariant={LineItemSizeVariant.small}
                />
              </InlineLineItemContainer>
            );
          })}
        </PriceBreakdown>
      </BreakdownSection>
      <DisclaimerText color={theme.color.grey60} variant={ResponsiveTypographyVariant.micro}>
        {cancellationSummary.editorial.disclaimer}
      </DisclaimerText>

      <Layout.Container justifyContent={'flex-end'}>
        {!isMobile && (
          <OptionsButton variant={ButtonVariant.secondary} onClick={handleToggleCreditRefund}>
            {`${
              cancellationSummary.alternativeRefundMethods[0]?.enticementText
                ? cancellationSummary.alternativeRefundMethods[0].enticementText
                : ''
            }
            ${cancellationSummary.alternativeRefundMethods[0].enticementCTA}`}
          </OptionsButton>
        )}
        <WrapperDiv>
          <StyledFloatingMessage
            variant={FloatingMessageVariant.PopupTip}
            showCloseButton={false}
            fullScreen={isMobile}
            title={i18n('cancellation.sure.cancel', [], translations)}
            content={CancellationConfirmation(
              handleConfirm,
              creditRefund,
              cancellationRequested,
              handleClose,
              theme
            )}
          >
            {(floatingMessageOpen: TEventFunction, floatingMessageClose: TEventFunction) => {
              popupTipClose.current = floatingMessageClose;

              return (
                <StyledButton
                  variant={ButtonVariant.primary}
                  onClick={floatingMessageOpen}
                  fullWidth={isMobile}
                  dataAttributes={{ testid: 'confirmCancelButton' }}
                >
                  {cancellationSummary.editorial.mainCTA}
                </StyledButton>
              );
            }}
          </StyledFloatingMessage>
        </WrapperDiv>
      </Layout.Container>

      {isMobile && (
        <OptionsButton variant={ButtonVariant.secondary} onClick={handleToggleCreditRefund}>
          {`${
            cancellationSummary.alternativeRefundMethods[0]?.enticementText
              ? cancellationSummary.alternativeRefundMethods[0].enticementText
              : ''
          }
            ${cancellationSummary.alternativeRefundMethods[0].enticementCTA}`}
        </OptionsButton>
      )}

      {isMobile && (
        <BackButton variant={ButtonVariant.text} onClick={handleCancel}>
          {`${i18n('cancellation.back.to.bookings', [], translations)}`}
        </BackButton>
      )}
    </React.Fragment>
  );
};

export default withTheme(CancellationSummary);
